import { createFileRoute, useNavigate, notFound } from '@tanstack/react-router';
import { Button } from 'primereact/button';
import { useState, useEffect } from 'react';
import { canAccess } from '~/utils/rbac';
import { AddEditDashboardDialog } from '~/components/ui/dashboard/AddEditDashboardDialog';
import { useCreateDashboard, useDashboards } from '~/hooks/useDashboard';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { DEFAULT_DASHBOARD_LAYOUT_SL } from '~/constants/constants';
import { useFeatureFlagsFromClerk } from '~/hooks/useOrganizationConfig';
import {
  usesDashboardNavigation,
  isFeatureEnabled,
} from '~/config/organizationConfig';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

export const Route = createFileRoute('/_authLayout/no-dashboards')({
  beforeLoad: async ({ context: _context }) => {
    if (!canAccess('dashboard')) {
      throw notFound();
    }
  },
  component: NoDashboardsPage,
});

function NoDashboardsPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { isLoaded: isOrgLoaded } = useOrganization();
  const featureFlags = useFeatureFlagsFromClerk();
  const createDashboard = useCreateDashboard();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const isDynamicDashboardEnabled = isFeatureEnabled(
    featureFlags,
    FEATURE_FLAGS.DYNAMIC_DASHBOARD,
  );

  const {
    data: dashboardsResponse,
    isLoading: dashboardsLoading,
    isError: dashboardsError,
  } = useDashboards();

  // No-dashboards is only for dynamic-dashboard orgs; redirect others to home
  useEffect(() => {
    if (isOrgLoaded && !isDynamicDashboardEnabled) {
      navigate({ to: '/' });
    }
  }, [isOrgLoaded, isDynamicDashboardEnabled, navigate]);

  // Redirect if user has dashboards
  useEffect(() => {
    if (
      !dashboardsLoading &&
      !dashboardsError &&
      dashboardsResponse &&
      auth.isLoaded
    ) {
      const useDashboardNav = usesDashboardNavigation(featureFlags);

      if (useDashboardNav) {
        // For dashboard-based orgs: check if dashboards exist, redirect to first one
        if (
          Array.isArray(dashboardsResponse) &&
          dashboardsResponse.length > 0
        ) {
          const firstDashboard = dashboardsResponse[0];
          if (firstDashboard && firstDashboard.id) {
            navigate({ to: `/dashboard/${firstDashboard.id}` });
          }
        }
      }
    }
  }, [
    auth,
    featureFlags,
    dashboardsError,
    dashboardsLoading,
    dashboardsResponse,
    navigate,
  ]);

  const handleCreateDashboard = () => {
    setShowCreateDialog(true);
  };

  const handleDialogSubmit = async (data: {
    name: string;
    icon?: string | null;
    id?: string;
    isDefault: boolean;
    selectedPortfolioId?: string;
    useDefaultLayout?: boolean;
  }) => {
    createDashboard.mutate(
      {
        name: data.name,
        config: data.useDefaultLayout ? DEFAULT_DASHBOARD_LAYOUT_SL : [],
        icon: data.icon || undefined,
        portfolioId: data.selectedPortfolioId,
      },
      {
        onSuccess: (response) => {
          if (response?.id) {
            navigate({ to: `/dashboard/${response.id}` });
          }
        },
        onError: (error) => {
          console.error('Failed to create dashboard:', error);
        },
      },
    );
  };

  if (!isOrgLoaded || !isDynamicDashboardEnabled) {
    return null;
  }

  // Show loading while checking for dashboards
  if (dashboardsLoading || !auth.isLoaded) {
    return (
      <div className="flex align-items-center justify-content-center min-h-screen">
        <div className="text-center">
          <h2>Loading...</h2>
          <p>Checking for dashboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-content-center align-items-center min-h-screen p-4">
      <div
        style={{
          width: 'auto',
          maxWidth: '56rem',
          backgroundColor:
            'var(--bq-surface-card, var(--bq-color-gray-light2, #141414))',
          border:
            '1px solid var(--bq-border-muted, var(--bq-color-gray-border))',
          borderRadius: '4px',
          boxShadow:
            '0 20px 35px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(15, 23, 42, 0.08)',
          color: 'var(--bq-text-primary, #1f2937)',
        }}
      >
        <div className="text-center p-6">
          <div className="mb-4">
            <i className="pi pi-chart-bar text-6xl text-400"></i>
          </div>

          <h2 className="text-2xl font-bold text-900 mb-3">
            No Dashboards Found
          </h2>

          <p className="text-600 mb-4 line-height-3">
            There are no dashboards available. Create a new dashboard to start
            viewing and analyzing your data.
          </p>

          <div className="flex justify-content-center">
            <Button
              label="Create New Dashboard"
              icon="pi pi-plus"
              onClick={handleCreateDashboard}
              size="large"
            />
          </div>
        </div>
      </div>

      <AddEditDashboardDialog
        visible={showCreateDialog}
        onHide={() => setShowCreateDialog(false)}
        onSubmit={handleDialogSubmit}
        mode="create"
      />
    </div>
  );
}
